/**
 * @file router.tsx
 * @description Route map. ProtectedRoute is a UX guard, real authz is
 *   API-enforced. Entry pages ship in the main chunk; rest is lazy-loaded.
 * @module App
 * @security Route-level checks are convenience; the API is authoritative.
 */

import { lazy, Suspense, type ComponentType, type JSX, type LazyExoticComponent } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

/** Wraps `lazy()` with a single-shot hard reload on dynamic-import failure
 *  (stale chunk after redeploy). Reload-loop guarded via sessionStorage. */
function lazyWithReload<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (err) {
      const KEY = 'chunk-reload-at';
      const last = Number(sessionStorage.getItem(KEY) ?? '0');
      if (Date.now() - last < 10_000) throw err;
      sessionStorage.setItem(KEY, String(Date.now()));
      window.location.reload();
      return new Promise<{ default: T }>(() => {});
    }
  });
}
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import NotFoundPage from './pages/NotFoundPage';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { SkeletonRows } from './components/common/Skeleton';

// Lazy chunks — each becomes its own `pnpm build` file, reload-on-stale-chunk.
const SettingsPage = lazyWithReload(() => import('./pages/SettingsPage'));
const AdminInvitationsPage = lazyWithReload(() => import('./pages/AdminInvitationsPage'));
const AdminAuditPage = lazyWithReload(() => import('./pages/AdminAuditPage'));
const AdminOpsStatusPage = lazyWithReload(() => import('./pages/AdminOpsStatusPage'));
const AdminFilesReviewPage = lazyWithReload(() => import('./pages/AdminFilesReviewPage'));
const AllProjectsPage = lazyWithReload(() => import('./pages/AllProjectsPage'));
const CustomerDetailPage = lazyWithReload(() => import('./pages/CustomerDetailPage'));
const NewProjectPage = lazyWithReload(() => import('./pages/NewProjectPage'));
const ProjectDetailPage = lazyWithReload(() => import('./pages/ProjectDetailPage'));
const QuotePrintPage = lazyWithReload(() => import('./pages/QuotePrintPage'));
const HelpPage = lazyWithReload(() => import('./pages/HelpPage'));

// WorkPilot (Guy's personal-OS overlay) — each section is its own lazy
// chunk so the customer-facing bundle is unaffected by this surface.
const WorkPilotHomePage = lazyWithReload(() => import('./pages/workpilot/WorkPilotHomePage'));
const WorkPilotSectionsModule = () => import('./pages/workpilot/WorkPilotPlaceholderPage');
// Tasks is now a real page (Phase 2A) — swapped from placeholder.
const WorkPilotTasksPage = lazyWithReload(() => import('./pages/workpilot/WorkPilotTasksPage'));
const WorkPilotExpensesPage = lazyWithReload(
  () => import('./pages/workpilot/WorkPilotExpensesPage'),
);
const WorkPilotCalendarPage = lazyWithReload(async () => ({
  default: (await WorkPilotSectionsModule()).WorkPilotCalendarPage,
}));
const WorkPilotGmailPage = lazyWithReload(async () => ({
  default: (await WorkPilotSectionsModule()).WorkPilotGmailPage,
}));
const WorkPilotGoalsPage = lazyWithReload(async () => ({
  default: (await WorkPilotSectionsModule()).WorkPilotGoalsPage,
}));
const WorkPilotReportsPage = lazyWithReload(async () => ({
  default: (await WorkPilotSectionsModule()).WorkPilotReportsPage,
}));
const WorkPilotVaultPage = lazyWithReload(async () => ({
  default: (await WorkPilotSectionsModule()).WorkPilotVaultPage,
}));
const WorkPilotAiPage = lazyWithReload(async () => ({
  default: (await WorkPilotSectionsModule()).WorkPilotAiPage,
}));
const WorkPilotProjectsListPage = lazyWithReload(async () => ({
  default: (await WorkPilotSectionsModule()).WorkPilotProjectsListPage,
}));

function RouteLoading(): JSX.Element {
  return (
    <div className="mx-auto max-w-4xl p-6">
      <SkeletonRows count={4} rowClassName="h-16 w-full" />
    </div>
  );
}

const router = createBrowserRouter([
  { path: '/', element: <LandingPage /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/privacy', element: <PrivacyPolicyPage /> },
  { path: '/terms', element: <TermsPage /> },
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <DashboardPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <SettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/invitations',
    element: (
      <ProtectedRoute>
        <AdminInvitationsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/audit',
    element: (
      <ProtectedRoute>
        <AdminAuditPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/ops',
    element: (
      <ProtectedRoute>
        <AdminOpsStatusPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/files-review',
    element: (
      <ProtectedRoute>
        <AdminFilesReviewPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/projects',
    element: (
      <ProtectedRoute>
        <AllProjectsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/customers/:id',
    element: (
      <ProtectedRoute>
        <CustomerDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/customers/:customerId/projects/new',
    element: (
      <ProtectedRoute>
        <NewProjectPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/projects/new',
    element: (
      <ProtectedRoute>
        <NewProjectPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/projects/:id',
    element: (
      <ProtectedRoute>
        <ProjectDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/quotes/:id/print',
    element: (
      <ProtectedRoute>
        <QuotePrintPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/help',
    element: (
      <ProtectedRoute>
        <HelpPage />
      </ProtectedRoute>
    ),
  },
  // WorkPilot surface — Guy's personal-OS overlay
  {
    path: '/workpilot',
    element: (
      <ProtectedRoute>
        <WorkPilotHomePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/workpilot/projects',
    element: (
      <ProtectedRoute>
        <WorkPilotProjectsListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/workpilot/expenses',
    element: (
      <ProtectedRoute>
        <WorkPilotExpensesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/workpilot/tasks',
    element: (
      <ProtectedRoute>
        <WorkPilotTasksPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/workpilot/calendar',
    element: (
      <ProtectedRoute>
        <WorkPilotCalendarPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/workpilot/gmail',
    element: (
      <ProtectedRoute>
        <WorkPilotGmailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/workpilot/goals',
    element: (
      <ProtectedRoute>
        <WorkPilotGoalsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/workpilot/reports',
    element: (
      <ProtectedRoute>
        <WorkPilotReportsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/workpilot/vault',
    element: (
      <ProtectedRoute>
        <WorkPilotVaultPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/workpilot/ai',
    element: (
      <ProtectedRoute>
        <WorkPilotAiPage />
      </ProtectedRoute>
    ),
  },
  { path: '*', element: <NotFoundPage /> },
]);

export function AppRouter(): JSX.Element {
  // One Suspense boundary wraps the whole router. Only fires when navigating
  // to a route whose page is lazy-loaded — eager routes (Landing, Dashboard,
  // etc.) never trigger it.
  return (
    <Suspense fallback={<RouteLoading />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}
